import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { platformApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import { PageLoading } from '@/components/ui/loading';
import { formatDate } from '@/lib/utils';
import { Plus, Building2, ArrowRight } from 'lucide-react';
import type { PlatformOrganization } from '@/lib/types';

export default function OrganizationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'organizations'],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: PlatformOrganization[] }>('/platform/organizations?limit=100');
      return r.data ?? [];
    },
  });

  const { page, setPage, pageSize, total, pageItems } = useClientPagination(data ?? []);

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-primary">Organizations</h1>
        <Link to="/super-admin/organizations/new"><Button className="gap-2"><Plus size={16} />New Organization</Button></Link>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableIndexHead />
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact Email</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((o, index) => (
                <TableRow key={o.id}>
                  <TableIndexCell value={rowSerial(index, page, pageSize)} />
                  <TableCell className="font-mono font-medium">{o.organization_code}</TableCell>
                  <TableCell className="font-medium text-primary">{o.name}</TableCell>
                  <TableCell className="text-sm text-secondary">{o.email || '—'}</TableCell>
                  <TableCell className="text-sm">{o.user_count ?? 0}</TableCell>
                  <TableCell>
                    <Badge variant={o.status === 'ACTIVE' ? 'approved' : 'rejected'}>{o.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted">{formatDate(o.created_at)}</TableCell>
                  <TableCell>
                    <Link to={`/super-admin/organizations/${o.id}`}>
                      <Button variant="ghost" size="icon" aria-label="View organization"><ArrowRight size={16} /></Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {(data ?? []).length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted">No organizations found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {(data ?? []).length > 0 && <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />}
        </CardContent>
      </Card>

      {(data ?? []).length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-muted">
          <Building2 size={48} className="mb-3 opacity-40" />
          <p>No organizations yet</p>
          <Link to="/super-admin/organizations/new" className="mt-3"><Button variant="outline" size="sm" className="gap-2"><Plus size={14} />Create Organization</Button></Link>
        </div>
      )}
    </div>
  );
}
